//@ts-check
import { Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button.jsx';
import { useUploadQueue } from '@/hooks/useUploadQueue.jsx';
import { cn } from '@/lib/utils.js';

/**
 * Floating panel showing background upload queue progress.
 * @returns {import('react').ReactElement|null}
 */
export function UploadQueuePanel() {
  const { items, retry, dismiss } = useUploadQueue();
  const active = items.filter((it) => it.status !== 'completed' || it.progress < 100);
  const visible = items.slice(-8);
  if (visible.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-border bg-white shadow-lg">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Upload className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">Uploads ({active.length} active)</p>
      </div>
      <ul className="max-h-72 space-y-2 overflow-auto p-3">
        {visible.map((it) => (
          <li key={it.localId} className="rounded-md border border-border p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-mono text-xs text-primary">{it.trackingId}</p>
                <p className="text-[11px] capitalize text-muted-foreground">{it.status}</p>
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => dismiss(it.localId)}
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded bg-slate-100">
              <div
                className={cn(
                  'h-full transition-all',
                  it.status === 'failed' ? 'bg-destructive' : 'bg-primary',
                )}
                style={{ width: `${it.progress}%` }}
              />
            </div>
            {it.error ? <p className="mt-1 text-[11px] text-destructive">{it.error}</p> : null}
            {it.status === 'failed' ? (
              <Button
                size="sm"
                variant="outline"
                className="mt-2 h-7 w-full text-xs"
                onClick={() => retry(it.localId)}
              >
                Retry
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
